/**
 * Author: Romain Hapi Sr.
 * Version: 1.0
 * Date: 2026-06-25
 * Project: Prenim Digital
 * Copyright: Prenim UK
 */
/**
 * Page d'accueil du site UK.
 *
 * Elle enchaine hero, annonce FaDel, introduction, services, chiffres, projets
 * et bande d'appel a l'action. Son hero passe volontairement SOUS la barre de
 * navigation fixe — d'ou l'absence du `pt-20` que portent les autres pages.
 */
import React from "react";
import { Link } from "react-router-dom";
import { useLang } from "../i18n/LanguageContext";
import SEO from "../components/SEO";
import MEDIA, { SERVICE_IMAGES } from "../lib/media";
import CalendlyButton from "../components/CalendlyButton";
import FadelLaunch from "../components/FadelLaunch";
import ProductCards from "../components/ProductCards";
import { PRODUCT_UI, tx } from "../lib/products";
import {
  ArrowRight,
  MapPin,
  Phone,
  Mail,
  CheckCircle2,
} from "lucide-react";

const Home = () => {
  const { t, lang } = useLang();
  if (!t) return null;
  const meta = t.meta.pages.home;
  const services = t.services.items;
  const projects = t.projects.items;

  return (
    <main data-testid="home-page">
      <SEO title={meta.title} description={meta.description} lang={lang} />

      {/* HERO */}
      <section
        data-testid="home-hero"
        className="relative min-h-[88vh] flex items-center overflow-hidden bg-slate-950 text-white"
      >
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url('${MEDIA.hero_background}')` }}
        />
        <div className="absolute inset-0 bg-gradient-to-br from-slate-950/95 via-slate-950/80 to-slate-900/70" />
        <div className="absolute inset-0 bg-grid-slate opacity-30" />

        <div className="relative z-10 max-w-site mx-auto px-4 sm:px-6 lg:px-8 py-32 w-full">
          <div className="max-w-3xl animate-fade-up">
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 border border-white/20 text-xs font-medium text-cyan-300 mb-6 backdrop-blur-sm">
              <span className="h-1.5 w-1.5 rounded-full bg-cyan-400 animate-pulse-ring" />
              {t.home.hero.eyebrow}
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-7xl font-bold tracking-tight leading-[1.05] mb-6">
              {t.home.hero.title}
            </h1>
            <p className="text-lg lg:text-xl text-slate-300 leading-relaxed max-w-2xl mb-10">
              {t.home.hero.subtitle}
            </p>
            <div className="flex flex-wrap gap-3">
              <Link
                to="/services"
                data-testid="hero-cta-primary"
                className="inline-flex items-center gap-2 rounded-full bg-blue-600 hover:bg-blue-500 text-white font-semibold px-6 py-3 transition-colors"
              >
                {t.home.hero.primary_cta}
                <ArrowRight className="h-4 w-4 rtl-flip" />
              </Link>
              <CalendlyButton
                variant="primary"
                testId="hero-cta-calendly"
              >
                {t.common.book_consultation}
              </CalendlyButton>
              <Link
                to="/contact"
                data-testid="hero-cta-secondary"
                className="inline-flex items-center gap-2 rounded-full border-2 border-white/30 hover:border-cyan-400 hover:text-cyan-400 text-white font-semibold px-6 py-3 transition-colors"
              >
                {t.home.hero.secondary_cta}
              </Link>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="absolute bottom-0 inset-x-0 z-10 border-t border-white/10 bg-slate-950/70 backdrop-blur-md">
          <div className="max-w-site mx-auto px-4 sm:px-6 lg:px-8 py-6 grid grid-cols-2 md:grid-cols-4 gap-6">
            {t.home.stats.map((s, i) => (
              <div key={i} data-testid={`hero-stat-${i}`} className="flex flex-col">
                <span className="text-3xl md:text-4xl font-bold text-white tracking-tight">
                  {s.value}
                </span>
                <span className="text-xs text-slate-400 mt-1 uppercase tracking-wider">
                  {s.label}
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* LANCEMENT FADEL */}
      <FadelLaunch />

      {/* INTRO */}
      <section data-testid="home-intro" className="py-24 lg:py-32">
        <div className="max-w-site mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <span className="text-xs font-semibold uppercase tracking-widest text-blue-600">
              {t.home.intro.eyebrow}
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-slate-950 mt-3 leading-[1.1]">
              {t.home.intro.title}
            </h2>
          </div>
          <div className="lg:col-span-7">
            <p className="text-lg text-slate-600 leading-relaxed">{t.home.intro.body}</p>
            <ul className="mt-8 grid sm:grid-cols-2 gap-4">
              {[
                t.about.values[0]?.title,
                t.about.values[1]?.title,
                t.about.values[2]?.title,
                t.about.values[3]?.title,
              ]
                .filter(Boolean)
                .map((v, i) => (
                  <li key={i} className="flex items-center gap-3 text-slate-700">
                    <CheckCircle2 className="h-5 w-5 text-cyan-500 shrink-0" />
                    <span className="font-medium">{v}</span>
                  </li>
                ))}
            </ul>
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section data-testid="home-services" className="py-24 lg:py-28 bg-slate-50">
        <div className="max-w-site mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mb-16">
            <span className="text-xs font-semibold uppercase tracking-widest text-blue-600">
              {t.home.services_section.eyebrow}
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-slate-950 mt-3">
              {t.home.services_section.title}
            </h2>
            <p className="mt-4 text-lg text-slate-600">{t.home.services_section.subtitle}</p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((s) => {
              return (
                <Link
                  key={s.slug}
                  to={`/services/${s.slug}`}
                  data-testid={`home-service-${s.slug}`}
                  className="group relative bg-white border border-slate-200 rounded-2xl p-7 card-lift"
                >
                  <div className="relative h-16 w-16 rounded-xl overflow-hidden mb-5 ring-1 ring-slate-200 group-hover:ring-blue-500 transition-all">
                    <img
                      src={SERVICE_IMAGES[s.slug]}
                      alt={s.title}
                      className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-tr from-slate-950/40 to-transparent group-hover:from-blue-600/40 transition-colors" />
                  </div>
                  <h3 className="text-xl font-semibold text-slate-950 mb-2">{s.title}</h3>
                  <p className="text-slate-600 text-sm leading-relaxed mb-5">{s.short}</p>
                  <span className="inline-flex items-center gap-1 text-sm font-semibold text-blue-600 group-hover:gap-2 transition-all">
                    {t.common.learn_more}
                    <ArrowRight className="h-4 w-4 rtl-flip" />
                  </span>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* PRESENCE */}
      <section data-testid="home-presence" className="py-24 lg:py-28 bg-slate-950 text-white relative overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-30"
          style={{ backgroundImage: `url('${MEDIA.map_reach}')` }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-slate-950 via-slate-950/85 to-transparent" />

        <div className="relative max-w-site mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6">
            <span className="text-xs font-semibold uppercase tracking-widest text-cyan-400">
              {t.home.presence.eyebrow}
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight mt-3 leading-[1.1]">
              {t.home.presence.title}
            </h2>
            <p className="mt-5 text-lg text-slate-300 leading-relaxed max-w-xl">
              {t.home.presence.body}
            </p>
          </div>
          <div className="lg:col-span-6 grid sm:grid-cols-2 gap-5">
            {[t.home.presence.uk, t.home.presence.td].map((office, i) => (
              <div
                key={i}
                data-testid={`presence-office-${i}`}
                className="rounded-2xl border border-white/10 bg-white/5 backdrop-blur-md p-6 hover:border-cyan-400/50 transition-colors"
              >
                <span className="text-xs font-semibold uppercase tracking-widest text-cyan-400">
                  {office.title}
                </span>
                <h3 className="text-2xl font-bold mt-2 mb-3">{office.city}</h3>
                <p className="flex items-start gap-2 text-sm text-slate-300 mb-2">
                  <MapPin className="h-4 w-4 mt-0.5 shrink-0 text-cyan-400" />
                  {office.address}
                </p>
                <p className="flex items-center gap-2 text-sm text-slate-300 mb-2">
                  <Phone className="h-4 w-4 shrink-0 text-cyan-400" />
                  {office.phone}
                </p>
                {office.phone2 && (
                  <p className="flex items-center gap-2 text-sm text-slate-300 mb-2">
                    <Phone className="h-4 w-4 shrink-0 text-cyan-400" />
                    {office.phone2}
                  </p>
                )}
                <p className="flex items-center gap-2 text-sm text-slate-300">
                  <Mail className="h-4 w-4 shrink-0 text-cyan-400" />
                  {office.email}
                </p>
                <p className="text-xs text-slate-500 mt-4 uppercase tracking-wider">{office.since}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PRODUCTS */}
      <section data-testid="home-products" className="py-24 lg:py-28 bg-slate-50">
        <div className="max-w-site mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row lg:items-end justify-between mb-14 gap-6">
            <div className="max-w-2xl">
              <span className="text-xs font-semibold uppercase tracking-widest text-blue-600">
                {tx(PRODUCT_UI.eyebrow, lang)}
              </span>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-slate-950 mt-3">
                {tx(PRODUCT_UI.sectionTitle, lang)}
              </h2>
              <p className="mt-4 text-lg text-slate-600">{tx(PRODUCT_UI.sectionSubtitle, lang)}</p>
            </div>
            <Link
              to="/projects"
              data-testid="home-products-cta"
              className="inline-flex items-center gap-2 text-sm font-semibold text-blue-600 hover:gap-3 transition-all"
            >
              {t.common.view_all_projects}
              <ArrowRight className="h-4 w-4 rtl-flip" />
            </Link>
          </div>
          <ProductCards lang={lang} limit={3} />
        </div>
      </section>

      {/* PROJECTS PREVIEW */}
      <section data-testid="home-projects" className="py-24 lg:py-28">
        <div className="max-w-site mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row lg:items-end justify-between mb-14 gap-6">
            <div className="max-w-2xl">
              <span className="text-xs font-semibold uppercase tracking-widest text-blue-600">
                {t.home.projects_section.eyebrow}
              </span>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-slate-950 mt-3">
                {t.home.projects_section.title}
              </h2>
              <p className="mt-4 text-lg text-slate-600">{t.home.projects_section.subtitle}</p>
            </div>
            <Link
              to="/projects"
              data-testid="home-projects-cta"
              className="inline-flex items-center gap-2 text-sm font-semibold text-blue-600 hover:gap-3 transition-all"
            >
              {t.common.view_all_projects}
              <ArrowRight className="h-4 w-4 rtl-flip" />
            </Link>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {projects.slice(0, 3).map((p) => (
              <article
                key={p.slug}
                data-testid={`home-project-${p.slug}`}
                className="group rounded-2xl border border-slate-200 overflow-hidden card-lift bg-white"
              >
                <div className="aspect-[4/3] overflow-hidden bg-slate-100">
                  <img
                    src={MEDIA[p.image_key]}
                    alt={p.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    loading="lazy"
                  />
                </div>
                <div className="p-6">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="px-2.5 py-1 rounded-full bg-amber-50 text-amber-700 text-xs font-semibold">
                      {p.country}
                    </span>
                    <span className="px-2.5 py-1 rounded-full bg-slate-100 text-slate-700 text-xs font-medium">
                      {p.domain}
                    </span>
                  </div>
                  <h3 className="text-lg font-semibold text-slate-950 mb-2">{p.title}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed line-clamp-3">{p.context}</p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* CTA BAND */}
      <section data-testid="home-cta-band" className="py-20 bg-gradient-to-br from-blue-600 to-blue-700 text-white">
        <div className="max-w-site mx-auto px-4 sm:px-6 lg:px-8 flex flex-col md:flex-row items-center justify-between gap-8">
          <div className="min-w-0">
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight">{t.home.cta_band.title}</h2>
            <p className="mt-3 text-blue-100 text-lg">{t.home.cta_band.subtitle}</p>
          </div>
          {/* `shrink-0` sur le groupe de boutons faisait deborder la page juste
              au passage en deux colonnes, vers 768 px : la rangee ne pouvait
              plus se resserrer. `flex-wrap` suffit a garder les boutons sur une
              ligne tant que la place existe. */}
          <div className="flex flex-wrap gap-3">
            <CalendlyButton variant="primary" testId="cta-band-calendly">
              {t.common.book_consultation_short}
            </CalendlyButton>
            <Link
              to="/contact"
              data-testid="home-cta-band-button"
              className="inline-flex items-center gap-2 rounded-full bg-white/10 hover:bg-white text-white hover:text-blue-700 border-2 border-white/30 hover:border-white font-semibold px-6 py-3 transition-colors"
            >
              {t.home.cta_band.cta}
              <ArrowRight className="h-4 w-4 rtl-flip" />
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
};

export default Home;
